// Agregador de signals do device.
//
// Signals são MEDIDOS — fato no mundo. Capabilities são DERIVADAS de
// signals. Caller consome capabilities, nunca signals diretos (exceto
// debug/telemetria).
//
// Bootstrapping:
// - Síncronos (CPU, RAM, viewport, features, platform): lidos no boot
//   imediatamente. Cached em module-level via `getSignalsSync()`.
// - Assíncronos (storage): lê via `loadAsyncSignals()` antes de capabilities
//   finais. Caller (bootstrap.ts) chama `await initSignals()` que faz
//   tudo numa tacada.

import type { Platform } from '@app-iptv/shared'

import { detectPlatformInfo } from '../platform'
import { loadNativePlatformInfo } from '../platform-native'

import { readCpuSignal, type CpuSignal } from './cpu'
import { readFeaturesSignal, type FeaturesSignal } from './features'
import { readNetworkSignal, type NetworkSignal } from './network'
import { readRamSignal, type RamSignal } from './ram'
import { readStorageSignal, type StorageSignal } from './storage'
import { readViewportSignal, type ViewportSignal } from './viewport'

export type {
  CpuSignal,
  CpuTier,
} from './cpu'
export type { FeaturesSignal } from './features'
export type { NetworkSignal, NetworkClass } from './network'
export { setLastMeasuredMbps, subscribeNetwork } from './network'
export type { RamSignal, RamTier } from './ram'
export type { StorageSignal, StorageTier } from './storage'
export type { ViewportSignal, ViewportTier } from './viewport'

export interface PlatformSignal {
  os: Platform
  /** Versão da plataforma. Em TV vem da API NATIVA (webOS `sdkVersion` via Luna,
   *  Tizen `platform.version`) — NÃO do UA, que a LG não preenche. `'0'` =
   *  desconhecida → `platform-tier` usa o `chromiumVersion`. */
  version: string
  /** True quando é TV nativa (Tizen/webOS/AndroidTV/FireTV). */
  isTV: boolean
  /** Chave de modelo conhecido (Fire TV `AFT*`, Shield, Mi Box) resolvida em
   *  `platform.ts`, ou o modelo comercial vindo da API nativa da TV (ex.:
   *  `'55SM8600PSA'`). null quando desconhecido. Alimenta `lookupKnownModel`. */
  model: string | null
  /** Firmware do device (webOS: `'05.50.00'`). Diagnóstico/telemetria — NÃO é a
   *  versão do webOS. */
  firmwareVersion: string | null
  /** Major do motor Chromium (ex.: 38, 53, 120). null quando o UA não expõe.
   *  Proxy determinístico de compositing/GPU — define a BANDA de tier em
   *  `platform-tier.ts` (e é o sinal principal quando a versão da plataforma
   *  não veio). */
  chromiumVersion: number | null
}

export interface DeviceSignals {
  cpu: CpuSignal
  ram: RamSignal
  storage: StorageSignal
  viewport: ViewportSignal
  network: NetworkSignal
  platform: PlatformSignal
  features: FeaturesSignal
  /** Timestamp ms (Date.now) de quando esta sessão começou (signals foram
   *  inicializados pela primeira vez). Usado pra capability `longSessionMode`
   *  detectar uso prolongado e disparar degradação progressiva. */
  sessionStartedAt: number
}

function readPlatformSignal(): PlatformSignal {
  const info = detectPlatformInfo()
  const isTV = ['tizen', 'webos', 'androidtv', 'firetv'].includes(info.platform)
  return {
    os: info.platform,
    version: info.version,
    isTV,
    model: info.model,
    firmwareVersion: info.firmwareVersion,
    chromiumVersion: info.chromiumVersion,
  }
}

// Cached module-level — boot inicializa, depois retorna estável.
// `null` = não inicializado ainda (pre-boot ou storage ainda async).
let _signals: DeviceSignals | null = null

/** Callback opcional disparado quando `initSignals` resolve E quando
 *  signals são recomputados. Capabilities subscribe daqui pra invalidar
 *  cache automaticamente — sem isso, caps cacheadas com fallback síncrono
 *  ficam congeladas mesmo após storage real chegar. */
const onSignalsRefreshed = new Set<() => void>()

/** Subscribe pra notificações de refresh de signals. Capabilities usa
 *  pra recomputar quando init/refresh roda. */
export function subscribeSignalsRefresh(cb: () => void): () => void {
  onSignalsRefreshed.add(cb)
  return () => onSignalsRefreshed.delete(cb)
}

function notifySignalsRefreshed(): void {
  // Itera cópia — defesa contra subscribers que se removem durante callback
  for (const cb of [...onSignalsRefreshed]) cb()
}

/** Inicializa signals. Idempotente — chamadas repetidas retornam cache.
 *  CALLER (bootstrap) deve chamar `await initSignals()` antes de qualquer
 *  `getCapabilities()` que precise de storage. Capabilities cacheadas
 *  com fallback síncrono são INVALIDADAS automaticamente quando init
 *  resolve (via `subscribeSignalsRefresh`). */
export async function initSignals(): Promise<DeviceSignals> {
  if (_signals) return _signals
  // `loadNativePlatformInfo` resolve a versão CANÔNICA da TV (webOS `sdkVersion`
  // via Luna — a LG não a põe no UA). Roda em paralelo com storage e é aguardado
  // AQUI, antes de `readPlatformSignal()`, pra que o tier já saia certo no 1º
  // render (o `bootstrap.ts` aguarda `initSignals()` antes de montar o React →
  // sem flash de tier). Nunca rejeita: no pior caso devolve o que já se sabia, e
  // o `chromiumVersion` do UA cobre sozinho.
  const [storage] = await Promise.all([readStorageSignal(), loadNativePlatformInfo()])
  // P4 (perf/tv-hero-coldstart): platform ANTES do cpu — readCpuSignal consulta a
  // BANDA de plataforma pra pular o microbench em device de banda travada (LG do
  // dono {mid,mid}, Tizen gen≤3, etc). Aqui a versão nativa da TV já resolveu
  // (loadNativePlatformInfo acima), então a banda sai canônica na 1ª medição.
  const platform = readPlatformSignal()
  _signals = {
    cpu: readCpuSignal(platform),
    ram: readRamSignal(),
    storage,
    viewport: readViewportSignal(),
    network: readNetworkSignal(),
    platform,
    features: readFeaturesSignal(),
    sessionStartedAt: Date.now(),
  }
  // Notifica capabilities (e qualquer outro subscriber) — caso `getCapabilities`
  // tenha sido chamado antes de init resolver, fallback síncrono é
  // substituído pelos signals reais.
  notifySignalsRefreshed()
  return _signals
}

/** Lê signals SÍNCRONOS (sem storage, que é async). Pra contextos onde
 *  storage ainda não foi resolvido. Capabilities que dependem de storage
 *  retornam estimativa conservadora até `initSignals` rodar.
 *
 *  Em produção, `initSignals()` no bootstrap é chamado ANTES do React
 *  montar — `getSignals()` (sync) sempre tem dados válidos.
 *  Em testing/SSR, retorna fallback. */
export function getSignals(): DeviceSignals {
  if (_signals) return _signals
  // Pré-init fallback: tudo síncrono possível, storage como 'unknown'.
  // sessionStartedAt = agora (vai ser sobrescrito quando initSignals
  // resolver, mas usabilidade pre-init não é cenário de long-session).
  // platform ANTES do cpu (P4): readCpuSignal usa a banda pra pular o bench
  // (mesmo pré-init, o chromiumVersion do UA já mapeia a geração da TV).
  const platform = readPlatformSignal()
  return {
    cpu: readCpuSignal(platform),
    ram: readRamSignal(),
    storage: { tier: 'unknown', quotaBytes: 0, usageBytes: 0 },
    viewport: readViewportSignal(),
    network: readNetworkSignal(),
    platform,
    features: readFeaturesSignal(),
    sessionStartedAt: Date.now(),
  }
}

/** Re-detecta network (rede mudou em runtime). Ré-roda só `readNetworkSignal`
 *  (outros são estáveis durante a sessão). Notifica subscribers — capabilities
 *  recomputa caps dependentes de rede (retryCount, defaultPlayerQuality, etc). */
export function refreshNetworkSignal(): void {
  if (!_signals) return
  _signals.network = readNetworkSignal()
  notifySignalsRefreshed()
}
